import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { useMetadataStatus, useRefreshMetadata } from '../../hooks/useMetadataStatus';
import { useSchemaChanges } from '../../hooks/useSchemaChanges';
import { useIntegratedMetadata } from '../../hooks/useIntegratedMetadata';
import { useTablesData } from '../../hooks/useTablesData';
import { useTableColumns } from '../../hooks/useTableColumns';
import { useMetadataTierStatus, useTableProfiling } from '../../hooks/useMetadataTiers';
import { transformTablesForHierarchy } from '../../utils/schemaHierarchy';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import HierarchicalDataExplorer from '../../components/common/HierarchicalDataExplorer';
import MetadataStatusPanel from './components/MetadataStatusPanel';
import MetadataTasksList from './components/MetadataTasksList';
import MetadataExplorer from './components/MetadataExplorer';
// import MetadataHistoryPanel from './components/MetadataHistoryPanel'; // TEMPORARILY REMOVED
import SchemaChangesPanel from './components/SchemaChangesPanel';
import RefreshControls from './components/RefreshControls';
import EmptyState from '../../components/common/EmptyState';
import TierProgressPanel from '../../components/metadata/TierProgressPanel';
import TierTriggerControls from '../../components/metadata/TierTriggerControls';
import {
  ServerIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  ChevronDownIcon,
  ChartBarIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Transform integrated metadata tables into hierarchical structure

const MetadataPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeConnection } = useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();
  const [selectedMetadataType, setSelectedMetadataType] = useState('tables');
  const [activeTab, setActiveTab] = useState('metadata'); // 'metadata', 'tiers', or 'schema-changes'
  
  // State for selected table metadata
  const [activeMetadataTab, setActiveMetadataTab] = useState('overview'); // 'overview', 'schema', 'profile', 'quality'
  
  // Get selected table from URL parameters
  const selectedTable = searchParams.get('table');
  const connectionId = activeConnection?.id;

  // Set breadcrumbs
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Metadata', href: '/metadata' }
    ]);
  }, [updateBreadcrumbs]);

  // Use useTableColumns hook for column data
  const columnsQuery = useTableColumns(connectionId, selectedTable, {
    enabled: !!(connectionId && selectedTable)
  });
  const tableColumns = columnsQuery.data || [];
  const isLoadingTableMetadata = columnsQuery.isLoading;

  // Fetch metadata status using the custom hook
  const {
    data: metadataStatus,
    isLoading: isLoadingStatus,
    refetch: refetchStatus
  } = useMetadataStatus(connectionId, {
    enabled: !!connectionId,
    refetchInterval: (data) => {
      // Refetch more frequently if there are pending tasks
      return (data?.pending_tasks?.length > 0) ? 5000 : 30000;
    }
  });

  // Use integrated metadata loading for better UX
  const {
    data: integratedData,
    isLoading: isLoadingIntegrated,
    refetch: refetchMetadata
  } = useIntegratedMetadata(connectionId, {
    enabled: !!connectionId && activeTab === 'metadata',
    includeColumns: true,
    includeStatistics: false
  });

  // Use direct tables data for table selector to preserve schema information
  const tablesQuery = useTablesData(connectionId, {
    enabled: !!connectionId,
    onError: (error) => {
      console.error('Error loading tables:', error);
    }
  });

  // Get table ID for selected table (needed for Tier 4 profiling API)
  const selectedTableId = React.useMemo(() => {
    if (!selectedTable || !tablesQuery.data?.tables) return null;

    // Match against full_name for multi-database support (e.g., "SPARVI.SANDBOX.ORDERS")
    // Fall back to matching just the name if full_name isn't available
    const table = tablesQuery.data.tables.find(t => {
      const fullName = t.database && t.schema && t.name
        ? `${t.database}.${t.schema}.${t.name}`
        : t.full_name;
      return fullName === selectedTable || t.name === selectedTable;
    });

    return table?._raw?.id || null;
  }, [selectedTable, tablesQuery.data]);

  // Fetch Tier 4 profiling data for selected table
  const profilingQuery = useTableProfiling(connectionId, selectedTableId, {
    enabled: !!(connectionId && selectedTableId)
  });

  // Calculate enhanced statistics from tableColumns and profiling data (tiered system)
  const enhancedStats = React.useMemo(() => {
    // Use profiling data from tiered system as primary source
    const profiling = profilingQuery.data;

    // Calculate primary key presence from columns
    const has_primary_key = tableColumns.some(col => col.is_primary_key === true);

    // Calculate nullable columns count
    const nullable_columns = tableColumns.filter(col => col.is_nullable === 'YES' || col.is_nullable === true).length;
    const non_nullable_columns = tableColumns.filter(col => col.is_nullable === 'NO' || col.is_nullable === false).length;

    // Calculate health score (0-100)
    let health_score = 0;
    let score_components = 0;

    // 1. Data completeness (25 points) - based on null percentages from tiered profiling
    if (profiling?.columns && profiling.columns.length > 0) {
      const columnsWithProfiling = profiling.columns.filter(col => col.profiling);
      if (columnsWithProfiling.length > 0) {
        const avgNullPercentage = columnsWithProfiling.reduce((sum, col) =>
          sum + (col.profiling?.null_percentage || 0), 0) / columnsWithProfiling.length;
        health_score += (100 - avgNullPercentage) * 0.25;
        score_components++;
      }
    }

    // 2. Primary key presence (25 points)
    if (has_primary_key) {
      health_score += 25;
    }
    score_components++;

    // 3. Column nullability (25 points) - prefer non-nullable
    if (tableColumns.length > 0) {
      const nonNullableRatio = non_nullable_columns / tableColumns.length;
      health_score += nonNullableRatio * 25;
      score_components++;
    }

    // 4. Data presence (25 points) - based on whether we have profiled data
    if (profiling?.isProfiled && profiling?.rowCount > 0) {
      health_score += 25;
    } else if (profiling?.rowCount > 0) {
      health_score += 15;
    }
    score_components++;

    // Normalize to 0-100 - always return a number, never null/undefined
    const final_score = score_components > 0 ? Math.round(health_score) : 0;

    return {
      // Row count and column stats from tiered profiling system
      general: {
        row_count: profiling?.rowCount || 0,
        column_count: profiling?.columns?.length || tableColumns.length
      },
      has_primary_key,
      nullable_columns,
      non_nullable_columns,
      health_score: final_score,
      // Freshness based on profiling timestamp
      freshness: profiling?.profiledAt ? {
        status: 'fresh',
        collected_at: profiling.profiledAt
      } : null,
      collection_metadata: {
        collected_at: profiling?.profiledAt || null
      }
    };
  }, [tableColumns, profilingQuery.data]);

  // Fetch tier status. Polling cadence is adaptive (handled inside the
  // hook): fast while a tier is running/queued, slow when idle. Avoids
  // the previous 30s flat poll that fired even when nothing was happening.
  const {
    data: tierStatus,
    isLoading: isLoadingTierStatus,
    refetch: refetchTierStatus
  } = useMetadataTierStatus(connectionId, {
    enabled: !!connectionId && activeTab === 'tiers',
  });

  // Extract data from integrated response
  const summary = integratedData?.success ? integratedData.data.summary : null;
  const hasColumns = integratedData?.success && integratedData.data.columns.length > 0;
  const hasBasicTables = integratedData?.success && integratedData.data.tables.length > 0;

  // Use the schema changes hook
  const {
    changes: schemaChanges,
    isLoading: isLoadingChanges,
    detectChanges,
    isDetecting,
    acknowledgeChanges,
    setAcknowledgedFilter,
    acknowledgedFilter
  } = useSchemaChanges(connectionId);

  // Use the refresh metadata mutation
  const {
    mutate: refreshMetadata,
    isPending: isRefreshing
  } = useRefreshMetadata(connectionId);

  // Check if there are unacknowledged schema changes
  const hasUnacknowledgedChanges = schemaChanges.some(change => !change.acknowledged);

  // Handle detecting schema changes
  const handleDetectChanges = () => {
    if (!connectionId) {
      showNotification('No connection selected', 'error');
      return;
    }

    detectChanges();
  };

  // Handle table selection
  const handleTableSelect = (table) => {
    // Use full_name (database.schema.table) to ensure correct table lookup in multi-database environments
    const tableName = table.full_name || table.object_name || table.name || table;
    // Update URL to include selected table using React Router
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('table', tableName);
    setSearchParams(newSearchParams);
  };

  // Enhanced refresh handler that works with integrated metadata
  const handleRefresh = (type, options = {}) => {
    const { onSuccess, onError } = options;

    if (type === 'full') {
      // Refresh all metadata types
      refreshMetadata({
        metadata_type: 'full',
        priority: 'high'
      }, {
        onSuccess: () => {
          // Also refresh our integrated metadata
          refetchMetadata();
          refetchStatus();
          onSuccess?.();
        },
        onError: (error) => {
          console.error('Error in full metadata refresh:', error);
          onError?.(error);
        }
      });
    } else {
      // Refresh specific metadata type
      refreshMetadata({
        metadata_type: type,
        priority: 'high'
      }, {
        onSuccess: () => {
          // Refresh integrated metadata after successful refresh
          refetchMetadata();
          refetchStatus();
          onSuccess?.();
        },
        onError: (error) => {
          console.error(`Error refreshing ${type} metadata:`, error);
          onError?.(error);
        }
      });
    }
  };

  // If no connection available
  if (!activeConnection) {
    return (
      <EmptyState
        icon={ServerIcon}
        title="No connection selected"
        description="Please select a database connection to view metadata"
        actionText="Manage Connections"
        actionLink="/connections"
      />
    );
  }

  // Handle loading state
  if (isLoadingStatus && !metadataStatus) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-secondary-600">Loading metadata information...</span>
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-secondary-900">
            {selectedTable ? `Metadata: ${selectedTable}` : 'Metadata Management'}
          </h1>
          {selectedTable && (
            <p className="text-sm text-gray-500 mt-1">
              View metadata information and schema changes for this object
            </p>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Cross-page navigation links */}
          {selectedTable && connectionId && activeConnection?.slug && (
            <div className="flex items-center space-x-2">
              <Link
                to={`/explorer/${activeConnection.slug}/tables/${encodeURIComponent(selectedTable)}`}
                className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="View detailed information about this table"
              >
                <EyeIcon className="-ml-1 mr-2 h-5 w-5 text-secondary-500" aria-hidden="true" />
                View in Data Explorer
              </Link>

              <Link
                to={`/validations?table=${encodeURIComponent(selectedTable)}&connection=${connectionId}`}
                className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                title="Manage validation rules for this table"
              >
                <ClipboardDocumentCheckIcon className="-ml-1 mr-2 h-5 w-5 text-secondary-500" aria-hidden="true" />
                View Validations
              </Link>
            </div>
          )}

          <div className="text-sm text-secondary-500">
            Connection: <span className="font-medium text-secondary-700">{activeConnection.name}</span>
          </div>
        </div>
      </div>

      {/* Enhanced summary panel when we have data - shows on all tabs for consistency */}
      {summary && (
        <div className="mt-4 bg-white border border-secondary-200 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary-900">{summary.total_tables || summary.totalTables || 0}</div>
              <div className="text-sm font-bold text-secondary-500">Tables</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{summary.total_columns || summary.totalColumns || 0}</div>
              <div className="text-sm font-bold text-secondary-500">Columns</div>
            </div>
          </div>

          {/* Loading indicator for progressive data */}
          {isLoadingIntegrated && (
            <div className="mt-3 flex items-center justify-center text-sm text-secondary-500">
              <LoadingSpinner size="sm" className="mr-2" />
              Loading detailed statistics...
            </div>
          )}
        </div>
      )}

      {/* Tab navigation */}
      <div className="border-b border-secondary-200 mt-4">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            className={`
              ${activeTab === 'metadata'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:border-secondary-300 hover:text-secondary-700'}
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
            `}
            onClick={() => setActiveTab('metadata')}
          >
            <TableCellsIcon className="mr-2 h-5 w-5" />
            Metadata Explorer
          </button>

          <button
            className={`
              ${activeTab === 'tiers'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:border-secondary-300 hover:text-secondary-700'}
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
            `}
            onClick={() => setActiveTab('tiers')}
          >
            <ChartBarIcon className="mr-2 h-5 w-5" />
            Tier Status
          </button>

          {/* TEMPORARILY REMOVED: Metadata History tab
          <button
            className={`
              ${activeTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:border-secondary-300 hover:text-secondary-700'}
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
            `}
            onClick={() => setActiveTab('history')}
          >
            <ClockIcon className="mr-2 h-5 w-5" />
            Metadata History
          </button>
          */}

          <button
            aria-label="Schema Changes"
            className={`
              ${activeTab === 'schema-changes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:border-secondary-300 hover:text-secondary-700'}
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
            `}
            onClick={() => setActiveTab('schema-changes')}
          >
            <ExclamationTriangleIcon className="mr-2 h-5 w-5" />
            Schema Changes
            {hasUnacknowledgedChanges && (
              <span className="ml-2 bg-warning-100 text-warning-800 text-xs px-2 py-0.5 rounded-full">
                {schemaChanges.filter(change => !change.acknowledged).length}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Add persistent banner for unacknowledged changes */}
      {hasUnacknowledgedChanges && activeTab !== 'schema-changes' && (
        <div className="mt-4 mb-6">
          <div className="rounded-md bg-warning-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-warning-400" aria-hidden="true" />
              </div>
              <div className="ml-3 flex-1 md:flex md:justify-between">
                <p className="text-sm text-warning-700">
                  There are {schemaChanges.filter(change => !change.acknowledged).length} unacknowledged schema changes that require your attention.
                </p>
                <p className="mt-3 text-sm md:mt-0 md:ml-6">
                  <button
                    onClick={() => setActiveTab('schema-changes')}
                    className="whitespace-nowrap font-medium text-warning-700 hover:text-warning-600"
                  >
                    View changes <span aria-hidden="true">&rarr;</span>
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Left Sidebar - Data Objects and Controls */}
        <div className="lg:col-span-3 space-y-6">
          {/* Data Objects Explorer - Always visible */}
          {tablesQuery.isLoading ? (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-center py-4">
                <LoadingSpinner size="md" />
                <span className="ml-3 text-secondary-600">Loading tables...</span>
              </div>
            </div>
          ) : (
            <HierarchicalDataExplorer
              data={transformTablesForHierarchy(tablesQuery.data?.tables || [], activeConnection)}
              onItemSelect={handleTableSelect}
              selectedItem={selectedTable}
              title="Data Objects"
              showCounts={true}
              className="shadow rounded-lg"
            />
          )}

          {/* Controls Panel - Context sensitive */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-3 border-b border-secondary-200">
              <h3 className="text-sm font-medium text-secondary-900">
                {activeTab === 'metadata' ? 'Refresh Controls' : 'Change Detection'}
              </h3>
            </div>
            <div className="p-4">
              {activeTab === 'metadata' ? (
                <RefreshControls
                  connectionId={connectionId}
                  onRefresh={handleRefresh}
                  isRefreshing={isRefreshing}
                  metadataStatus={metadataStatus}
                  onMetadataTypeSelect={setSelectedMetadataType}
                  selectedMetadataType={selectedMetadataType}
                  onViewSchemaChanges={() => setActiveTab('schema-changes')}
                />
              ) : (
                <div className="space-y-4">
                  <button
                    onClick={handleDetectChanges}
                    disabled={isDetecting}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {isDetecting ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Detecting Changes...
                      </>
                    ) : (
                      <>
                        <ExclamationTriangleIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                        Detect Schema Changes
                      </>
                    )}
                  </button>

                  <div className="border-t border-secondary-200 pt-4">
                    <div className="rounded-md bg-secondary-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <TableCellsIcon className="h-5 w-5 text-secondary-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-secondary-900">About Schema Changes</h3>
                          <div className="mt-2 text-sm text-secondary-700">
                            <p>
                              Schema changes are detected by comparing the current database schema with
                              the previously stored metadata. Detection can be triggered manually or
                              will run automatically during metadata refresh.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Active Tasks */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-3 border-b border-secondary-200">
              <h3 className="text-sm font-medium text-secondary-900">Active Tasks</h3>
            </div>
            <div className="p-4">
              <MetadataTasksList
                connectionId={connectionId}
                metadataStatus={metadataStatus}
                isLoading={isLoadingStatus}
              />
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9">
          <div className="bg-white shadow rounded-lg">
            {/* Metadata Status Panel */}
            <MetadataStatusPanel
              metadataStatus={metadataStatus}
              isLoading={isLoadingStatus}
              onRefresh={() => refetchStatus()}
            />

            <div className="px-4 py-5 sm:p-6 border-t border-secondary-200">
              {activeTab === 'metadata' || activeTab === 'tiers' ? (
                activeTab === 'metadata' ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-secondary-900 mb-4">Metadata Overview</h3>
                    <p className="text-sm text-secondary-600">
                      View and manage metadata information for your database objects. Select an object from the left panel to view detailed metadata.
                    </p>
                  </div>
                  
                  {/* Selected Table Metadata Details */}
                  {selectedTable && (
                    <div className="mt-6">
                      <div className="border-t border-secondary-200 pt-6">
                        <h3 className="text-lg font-medium text-secondary-900 mb-4">
                          Table Details: {selectedTable}
                        </h3>
                        
                        {/* Basic Table Information */}
                        <div className="bg-secondary-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-medium text-secondary-900 mb-2">Basic Information</h4>
                              <div className="space-y-1 text-sm">
                                <div><span className="text-secondary-500">Table Name:</span> <span className="font-medium">{selectedTable}</span></div>
                                <div><span className="text-secondary-500">Connection:</span> <span className="font-medium">{activeConnection?.name}</span></div>
                                <div><span className="text-secondary-500">Database:</span> <span className="font-medium">{activeConnection?.connection_details?.database || 'N/A'}</span></div>
                                <div><span className="text-secondary-500">Schema:</span> <span className="font-medium">{activeConnection?.connection_details?.schema || 'PUBLIC'}</span></div>
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="text-sm font-medium text-secondary-900 mb-2">Quick Actions</h4>
                              <div className="space-y-2">
                                <Link
                                  to={`/explorer/${activeConnection?.slug}/tables/${encodeURIComponent(selectedTable)}`}
                                  className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                  <EyeIcon className="-ml-1 mr-2 h-4 w-4 text-secondary-500" />
                                  View in Data Explorer
                                </Link>

                                <Link
                                  to={`/validations?table=${encodeURIComponent(selectedTable)}&connection=${connectionId}`}
                                  className="inline-flex items-center px-3 py-1.5 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ml-2"
                                >
                                  <ClipboardDocumentCheckIcon className="-ml-1 mr-2 h-4 w-4 text-secondary-500" />
                                  Manage Validations
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Modern Metadata Information with Tabs */}
                        <div className="bg-white border border-secondary-200 rounded-lg overflow-hidden">
                          {/* Tab Navigation */}
                          <div className="border-b border-secondary-200">
                            <nav className="flex space-x-8 px-6" aria-label="Tabs">
                              {[
                                { id: 'overview', name: 'Overview', icon: ChartBarIcon },
                                { id: 'schema', name: 'Schema', icon: TableCellsIcon },
                                { id: 'profile', name: 'Data Profile', icon: ClipboardDocumentCheckIcon },
                                { id: 'quality', name: 'Quality', icon: CheckCircleIcon }
                              ].map((tab) => (
                                <button
                                  key={tab.id}
                                  onClick={() => setActiveMetadataTab(tab.id)}
                                  className={`${
                                    activeMetadataTab === tab.id
                                      ? 'border-primary-500 text-primary-600'
                                      : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                                >
                                  <tab.icon className="h-4 w-4" />
                                  <span>{tab.name}</span>
                                </button>
                              ))}
                            </nav>
                          </div>

                          {/* Tab Content */}
                          <div className="p-6">
                            {isLoadingTableMetadata ? (
                              <div className="flex items-center justify-center py-12">
                                <LoadingSpinner size="md" />
                                <span className="ml-3 text-secondary-600">Loading table metadata...</span>
                              </div>
                            ) : (
                              <>
                                {/* Overview Tab */}
                                {activeMetadataTab === 'overview' && (
                                  <div className="space-y-6">
                                    {/* Key Metrics Cards */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                      {/* Row Count */}
                                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                              <span className="text-white text-sm font-bold">R</span>
                                            </div>
                                          </div>
                                          <div className="ml-3">
                                            <p className="text-sm font-medium text-blue-900">Total Rows</p>
                                            <p className="text-2xl font-bold text-blue-900">
                                              {enhancedStats?.general?.row_count ? enhancedStats.general.row_count.toLocaleString() : '-'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Column Count */}
                                      <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                                              <span className="text-white text-sm font-bold">C</span>
                                            </div>
                                          </div>
                                          <div className="ml-3">
                                            <p className="text-sm font-medium text-green-900">Columns</p>
                                            <p className="text-2xl font-bold text-green-900">{tableColumns.length}</p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Health Score */}
                                      <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                                              <span className="text-white text-sm font-bold">H</span>
                                            </div>
                                          </div>
                                          <div className="ml-3">
                                            <div className="flex items-center space-x-1">
                                              <p className="text-sm font-medium text-purple-900">Health Score</p>
                                              <div className="group relative">
                                                <InformationCircleIcon className="h-4 w-4 text-purple-600 cursor-help" />
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                  Calculated from data completeness, primary key presence, column nullability, and freshness
                                                </div>
                                              </div>
                                            </div>
                                            <p className="text-2xl font-bold text-purple-900">
                                              {enhancedStats?.health_score !== undefined && enhancedStats?.health_score !== null ? enhancedStats.health_score : '-'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Freshness */}
                                      <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                                              <span className="text-white text-sm font-bold">F</span>
                                            </div>
                                          </div>
                                          <div className="ml-3">
                                            <p className="text-sm font-medium text-orange-900">Freshness</p>
                                            <p className="text-lg font-bold text-orange-900">
                                              {enhancedStats?.freshness?.status || 'Unknown'}
                                            </p>
                                            {enhancedStats?.freshness?.age_days != null && (
                                              <p className="text-xs text-orange-700">
                                                {enhancedStats.freshness.age_days.toFixed(1)} days old
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Data Type Distribution */}
                                    {enhancedStats?.column_type_distribution && (
                                      <div className="bg-gray-50 rounded-lg p-4">
                                        <h3 className="text-lg font-medium text-gray-900 mb-3">Column Type Distribution</h3>
                                        <div className="flex items-center space-x-6">
                                          {Object.entries(enhancedStats.column_type_distribution).map(([type, count]) => (
                                            <div key={type} className="flex items-center space-x-2">
                                              <div className={`w-3 h-3 rounded-full ${{
                                                'text': 'bg-blue-400',
                                                'datetime': 'bg-green-400', 
                                                'other': 'bg-yellow-400',
                                                'numeric': 'bg-purple-400'
                                              }[type] || 'bg-gray-400'}`}></div>
                                              <span className="text-sm font-medium text-gray-700 capitalize">{type}</span>
                                              <span className="text-sm text-gray-500">({count})</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Quick Stats Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-3">Table Properties</h4>
                                        <div className="space-y-2 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Primary Key:</span>
                                            <span className={`font-medium ${enhancedStats?.has_primary_key ? 'text-green-600' : 'text-red-600'}`}>
                                              {enhancedStats?.has_primary_key ? 'Yes' : 'No'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Nullable Columns:</span>
                                            <span className="font-medium">{enhancedStats?.nullable_columns || 0}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Non-Nullable:</span>
                                            <span className="font-medium">{enhancedStats?.non_nullable_columns || 0}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500">Collected:</span>
                                            <span className="font-medium text-xs">
                                              {enhancedStats?.collection_metadata?.collected_at ? new Date(enhancedStats.collection_metadata.collected_at).toLocaleString() : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                                        <h4 className="font-medium text-gray-900 mb-3">Quick Actions</h4>
                                        <div className="space-y-2">
                                          <Link
                                            to={`/explorer/${activeConnection?.slug}/tables/${encodeURIComponent(selectedTable)}`}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                          >
                                            <EyeIcon className="h-4 w-4 mr-2" />
                                            View Data
                                          </Link>
                                          <Link
                                            to={`/validations?table=${encodeURIComponent(selectedTable)}&connection=${connectionId}`}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                          >
                                            <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                                            Manage Validations
                                          </Link>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Schema Tab */}
                                {activeMetadataTab === 'schema' && (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                      <h3 className="text-lg font-medium text-gray-900">Table Schema</h3>
                                      <span className="text-sm text-gray-500">{tableColumns.length} columns</span>
                                    </div>
                                    
                                    {/* Column Grid */}
                                    <div className="grid gap-3">
                                      {tableColumns.map((column, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                              <span className="text-sm font-medium text-gray-900">
                                                {column.name}
                                              </span>
                                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {column.type}
                                              </span>
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                column.nullable ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                                              }`}>
                                                {column.nullable ? 'NULL' : 'NOT NULL'}
                                              </span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                              {column.default !== 'None' ? `Default: ${column.default}` : 'No default'}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Data Profile Tab */}
                                {activeMetadataTab === 'profile' && (
                                  <div className="space-y-6">
                                    <h3 className="text-lg font-medium text-gray-900">Column Statistics</h3>

                                    {profilingQuery.data?.isProfiled && profilingQuery.data?.columns ? (
                                      <div className="grid gap-4">
                                        {profilingQuery.data.columns.map((column) => {
                                          const prof = column.profiling;
                                          if (!prof) return null;

                                          return (
                                            <div key={column.name} className="bg-white border border-gray-200 rounded-lg p-4">
                                              <div className="flex items-center justify-between mb-3">
                                                <h4 className="font-medium text-gray-900">{column.name}</h4>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                                  {column.data_type}
                                                </span>
                                              </div>
                                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                {prof.distinct_count !== undefined && (
                                                  <div>
                                                    <span className="text-gray-500">Distinct Values:</span>
                                                    <div className="font-medium">{prof.distinct_count.toLocaleString()}</div>
                                                  </div>
                                                )}
                                                {prof.null_percentage != null && (
                                                  <div>
                                                    <span className="text-gray-500">Null %:</span>
                                                    <div className="font-medium">{prof.null_percentage.toFixed(2)}%</div>
                                                  </div>
                                                )}
                                                {prof.min !== undefined && prof.min !== null && (
                                                  <div>
                                                    <span className="text-gray-500">Min:</span>
                                                    <div className="font-medium">{typeof prof.min === 'number' ? prof.min.toLocaleString() : prof.min}</div>
                                                  </div>
                                                )}
                                                {prof.max !== undefined && prof.max !== null && (
                                                  <div>
                                                    <span className="text-gray-500">Max:</span>
                                                    <div className="font-medium">{typeof prof.max === 'number' ? prof.max.toLocaleString() : prof.max}</div>
                                                  </div>
                                                )}
                                                {prof.avg !== undefined && prof.avg !== null && (
                                                  <div>
                                                    <span className="text-gray-500">Average:</span>
                                                    <div className="font-medium">{Number(prof.avg).toLocaleString()}</div>
                                                  </div>
                                                )}
                                                {prof.min_length !== undefined && (
                                                  <div>
                                                    <span className="text-gray-500">Min Length:</span>
                                                    <div className="font-medium">{prof.min_length}</div>
                                                  </div>
                                                )}
                                                {prof.max_length !== undefined && (
                                                  <div>
                                                    <span className="text-gray-500">Max Length:</span>
                                                    <div className="font-medium">{prof.max_length}</div>
                                                  </div>
                                                )}
                                                {prof.avg_length != null && (
                                                  <div>
                                                    <span className="text-gray-500">Avg Length:</span>
                                                    <div className="font-medium">{prof.avg_length.toFixed(1)}</div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : profilingQuery.isLoading ? (
                                      <div className="text-center py-8">
                                        <LoadingSpinner size="md" />
                                        <p className="mt-2 text-sm text-gray-500">Loading profiling data...</p>
                                      </div>
                                    ) : (
                                      <div className="text-center py-8">
                                        <ClipboardDocumentCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900">No profiling data available</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                          Column statistics haven't been collected yet.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Quality Tab */}
                                {activeMetadataTab === 'quality' && (
                                  <div className="space-y-6">
                                    <div className="text-center">
                                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="text-2xl font-bold text-purple-600">
                                          {enhancedStats?.health_score !== undefined && enhancedStats?.health_score !== null ? enhancedStats.health_score : '-'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-center space-x-2">
                                        <h3 className="text-lg font-medium text-gray-900">Table Health Score</h3>
                                        <div className="group relative">
                                          <InformationCircleIcon className="h-5 w-5 text-gray-500 cursor-help" />
                                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                            Calculated from data completeness, primary key presence, column nullability, and freshness
                                          </div>
                                        </div>
                                      </div>
                                      <p className="text-sm text-gray-500">Overall data quality assessment</p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                          {enhancedStats?.non_nullable_columns || 0}
                                        </div>
                                        <div className="text-sm text-green-700">Non-Nullable Columns</div>
                                      </div>

                                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                                        <div className="text-2xl font-bold text-yellow-600">
                                          {enhancedStats?.nullable_columns || 0}
                                        </div>
                                        <div className="text-sm text-yellow-700">Nullable Columns</div>
                                      </div>

                                      <div className={`border rounded-lg p-4 text-center ${
                                        enhancedStats?.has_primary_key
                                          ? 'bg-green-50 border-green-200'
                                          : 'bg-red-50 border-red-200'
                                      }`}>
                                        <div className={`text-2xl font-bold flex justify-center ${
                                          enhancedStats?.has_primary_key ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                          {enhancedStats?.has_primary_key
                                            ? <CheckIcon className="h-7 w-7" />
                                            : <XMarkIcon className="h-7 w-7" />}
                                        </div>
                                        <div className={`text-sm ${
                                          enhancedStats?.has_primary_key ? 'text-green-700' : 'text-red-700'
                                        }`}>
                                          Primary Key
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                ) : (
                  // Tier Status Tab Content
                  <div className="space-y-6">
                    <TierProgressPanel
                      tierStatus={tierStatus}
                      loading={isLoadingTierStatus}
                    />
                    <TierTriggerControls
                      connectionId={connectionId}
                      onTriggerSuccess={(tierType) => {
                        showNotification(
                          `${tierType === 'all' ? 'All tiers' : tierType} triggered successfully`,
                          'success'
                        );
                        // Refetch tier status after delays to catch updated "last run" times
                        // Background jobs need time to start and update the database
                        setTimeout(() => refetchTierStatus(), 3000);  // First check after 3 seconds
                        setTimeout(() => refetchTierStatus(), 8000);  // Second check after 8 seconds
                        setTimeout(() => refetchTierStatus(), 15000); // Final check after 15 seconds
                      }}
                    />
                  </div>
                )
              ) : (
                <SchemaChangesPanel
                  connectionId={connectionId}
                  schemaChanges={schemaChanges}
                  isLoading={isLoadingChanges}
                  onAcknowledge={acknowledgeChanges}
                  onRefresh={handleDetectChanges}
                  setAcknowledgedFilter={setAcknowledgedFilter}
                  acknowledgedFilter={acknowledgedFilter}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetadataPage;